import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Dumbbell, Check, ArrowRight, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

export default function FitnessCampPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", branch: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/fitness-camp/apply`, form);
      toast.success("Application submitted!");
      setSubmitted(true);
    } catch { toast.error("Failed to submit. Try again."); }
    setSubmitting(false);
  };

  return (
    <div data-testid="fitness-camp-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1607962837359-5e7e89f86776?crop=entropy&cs=srgb&fm=jpg&w=1600" alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Transform</p>
          </motion.div>
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-6">
            Veteran Fitness<br/>Camp
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="font-body text-base lg:text-lg text-zinc-400 max-w-xl leading-relaxed">
            Bad Company is partnering with elite trainers to offer discounted fitness programs for veterans. Plus, 3 free spots for selected applicants.
          </motion.p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 lg:py-24 bg-zinc-950">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="font-subheading text-3xl uppercase tracking-tight mb-10 text-center">What's Included</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Personal Training", desc: "Work with certified trainers who understand your goals", img: "https://images.unsplash.com/photo-1607962837359-5e7e89f86776?crop=entropy&cs=srgb&fm=jpg&w=400" },
              { title: "Nutrition Plan", desc: "Custom meal plans designed for optimal performance", img: "https://images.pexels.com/photos/8401961/pexels-photo-8401961.jpeg?auto=compress&cs=tinysrgb&w=400" },
              { title: "Team Building", desc: "Bond with your group through challenges and shared goals", img: "https://images.unsplash.com/photo-1748698534410-867798220fa8?crop=entropy&cs=srgb&fm=jpg&w=400" },
              { title: "Content Feature", desc: "Your transformation story documented and shared", img: "https://images.unsplash.com/photo-1540576701-439ee01f9b38?crop=entropy&cs=srgb&fm=jpg&w=400" },
            ].map((item, i) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
                className="border border-white/10 overflow-hidden">
                <div className="aspect-video overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="font-subheading text-sm uppercase tracking-wider mb-2">{item.title}</h3>
                  <p className="font-body text-xs text-zinc-500">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-subheading text-3xl uppercase tracking-tight mb-10 text-center">Veteran Pricing</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="border border-white/10 p-8 text-center">
              <span className="font-heading text-4xl">FREE</span>
              <p className="font-subheading text-sm uppercase tracking-wider mt-2 mb-4">Selected Applicants</p>
              <p className="font-body text-xs text-zinc-500 mb-4">3 spots available. Apply below.</p>
              <ul className="space-y-2 text-left mb-6">
                {["Full camp access", "Personal trainer", "Nutrition plan", "Video feature"].map(b => (
                  <li key={b} className="flex items-center gap-2 font-body text-xs text-zinc-400"><Check size={12} className="text-zinc-500" />{b}</li>
                ))}
              </ul>
              <span className="font-body text-[10px] uppercase tracking-wider text-zinc-600 border border-white/10 px-3 py-1">Application Required</span>
            </div>
            <div className="border border-white/10 p-8 text-center glow-border">
              <span className="font-body text-[10px] uppercase tracking-[0.2em] text-black bg-white px-3 py-1 inline-block mb-3">Best Value</span>
              <span className="font-heading text-4xl">50% OFF</span>
              <p className="font-subheading text-sm uppercase tracking-wider mt-2 mb-4">Veteran Discount</p>
              <p className="font-body text-xs text-zinc-500 mb-4">Available to all verified veterans</p>
              <ul className="space-y-2 text-left mb-6">
                {["Full camp access", "Group sessions", "Nutrition guidance", "Community access"].map(b => (
                  <li key={b} className="flex items-center gap-2 font-body text-xs text-zinc-400"><Check size={12} className="text-zinc-500" />{b}</li>
                ))}
              </ul>
              <Link to="/contact" className="font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-2.5 hover:bg-zinc-200 transition-colors inline-block">
                Contact Us
              </Link>
            </div>
            <div className="border border-white/10 p-8 text-center">
              <span className="font-heading text-4xl">SPONSOR</span>
              <p className="font-subheading text-sm uppercase tracking-wider mt-2 mb-4">Fund a Spot</p>
              <p className="font-body text-xs text-zinc-500 mb-4">Sponsor a veteran's camp experience</p>
              <ul className="space-y-2 text-left mb-6">
                {["Fund a vet's training", "Tax-deductible*", "Impact report", "Recognition"].map(b => (
                  <li key={b} className="flex items-center gap-2 font-body text-xs text-zinc-400"><Check size={12} className="text-zinc-500" />{b}</li>
                ))}
              </ul>
              <Link to="/sponsor-veteran" className="font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white transition-colors inline-block">
                Sponsor Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 lg:py-24 bg-zinc-950">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <Star size={28} className="text-zinc-400 mx-auto mb-4" />
            <h2 className="font-subheading text-3xl uppercase tracking-tight mb-3">Apply for a Free Spot</h2>
            <p className="font-body text-sm text-zinc-500">3 spots available for selected veteran applicants</p>
          </div>

          {submitted ? (
            <div className="border border-green-500/30 bg-green-500/10 p-8 text-center">
              <Check size={36} className="text-green-400 mx-auto mb-4" />
              <h3 className="font-subheading text-xl uppercase tracking-wider text-green-400 mb-2">Application Submitted!</h3>
              <p className="font-body text-sm text-zinc-400">We'll review your application and be in touch. Thank you for your service.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Full Name</label>
                  <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bc-input w-full" placeholder="Your name" data-testid="camp-name" />
                </div>
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Email</label>
                  <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bc-input w-full" placeholder="Email" data-testid="camp-email" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Phone</label>
                  <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bc-input w-full" placeholder="Phone" />
                </div>
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Branch of Service</label>
                  <input value={form.branch} onChange={e => setForm({...form, branch: e.target.value})} className="bc-input w-full" placeholder="Army, Navy, etc." />
                </div>
              </div>
              <div>
                <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Why do you want to participate?</label>
                <textarea required rows={4} value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} className="bc-input w-full resize-none" placeholder="Tell us your story and why this camp would make a difference..." data-testid="camp-reason" />
              </div>
              <button type="submit" disabled={submitting} data-testid="camp-submit"
                className="w-full font-subheading text-sm uppercase tracking-widest bg-white text-black px-8 py-3.5 hover:bg-zinc-200 transition-colors disabled:opacity-50">
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
